/**
 * WHAT A QUANTITY IS IN -- one wording for every place that has to say it.
 *
 * The sales manager, 4 Oct 2026: "Qty YTD (mixed)" was not clear. A quantity
 * that adds kilograms to litres to drums is one number standing for several
 * kinds of thing, and "(mixed)" alone did not say mixed WHAT. So the suffix now
 * names it, and the heading's tooltip says what that means in practice.
 *
 * Shared because three screens say it -- the Explorer's quantity headings, the
 * Invoice Lines quantity total, and Material Planning, which writes the unit
 * beside every figure, tile and total -- and copies of a caveat are how one of
 * them ends up saying something different. Material Planning's figures read
 * "mixed" on their own until the owner pointed at them the same day.
 */

/** The suffix a quantity label carries when its figures mix units. */
export const MIXED_UNITS_SUFFIX = ' (Mixed Units)'

/** The same words where they stand beside a figure rather than in a heading. */
export const MIXED_UNITS = 'mixed units'

/**
 * The tooltip behind those words.
 *
 * `known` is whichever units the screen can actually name -- a row that is
 * itself a mixture carries no unit of its own, so this can be a partial list,
 * or empty, in which case the sentence gives examples rather than a claim.
 * `count`, where the screen has it, is how many units the figure adds.
 */
export function mixedUnitsHint(known: Iterable<string>, count?: number | null): string {
  const units = [...new Set(known)].sort((a, b) => a.localeCompare(b))
  const list =
    units.length === 0
      ? 'for example kg, L and each'
      : units.length > 6
        ? `${units.slice(0, 6).join(', ')} and others`
        : units.join(', ')
  const how = count && count > 1 ? `${count} units of measure` : 'multiple units of measure'
  return (
    `Includes ${how} (${list}) added together as one number. ` +
    'It is meaningful within a single product; across a wider grouping it adds kilograms to litres.'
  )
}

/**
 * The suffix and tooltip for a column folded from many rows.
 *
 * MIXED when any row is itself a mixture (`unitMixed`) or the rows disagree;
 * the single unit when every row that names one names the same; nothing at all
 * when no unit is shown, which is a different statement from "one unit".
 */
export function quantityUnits(rows: { unit: string | null; unitMixed?: boolean | null }[]): {
  suffix: string
  hint?: string
} {
  const units = new Set(rows.map((r) => r.unit).filter((u): u is string => !!u))
  if (units.size > 1 || rows.some((r) => r.unitMixed === true)) {
    return { suffix: MIXED_UNITS_SUFFIX, hint: mixedUnitsHint(units) }
  }
  return units.size === 1 ? { suffix: ` (${[...units][0]!})` } : { suffix: '' }
}
